import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { __, CardComponent, MessengerService } from '@web/birdz-angular';
import { ModalAddToFolderComponent } from 'app/components/modal-add-to-folder/modal-add-to-folder.component';
import { ModalConfirmDeleteQueryComponent } from 'app/components/modal-confirm-delete-query/modal-confirm-delete-query.component';
import { ModalCreateFolderComponent } from 'app/components/modal-create-folder/modal-create-folder.component';
import { ModalDuplicateQueryComponent } from 'app/components/modal-duplicate-query/modal-duplicate-query.component';
import { Folder } from 'app/interfaces/folder.interface';
import { Query } from 'app/interfaces/query.interface';
import { FolderService } from 'app/services/folder.service';
import { QueryService } from 'app/services/query.service';
import { UserService } from 'app/services/user.service';
import { switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-page-query-list',
  templateUrl: './page-query-list.component.html',
  styleUrls: ['./page-query-list.component.scss'],
  imports: [
    CardComponent,
    NgClass,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatChipsModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
    TranslocoPipe,
  ],
})
export class PageQueryListComponent implements OnInit {
  protected queryService = inject(QueryService);
  protected folderService = inject(FolderService);
  private userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private messenger = inject(MessengerService);
  private destroyRef = inject(DestroyRef);

  readonly sortOptions: { key: 'name' | 'creator_email' | 'created_at'; labelKey: string }[] = [
    { key: 'name', labelKey: 'QUERY_SORT_NAME' },
    { key: 'creator_email', labelKey: 'QUERY_SORT_CREATOR' },
    { key: 'created_at', labelKey: 'QUERY_SORT_CREATED_AT' },
  ];

  queries: Query[] = [];
  folders: Folder[] = [];
  isLoading = false;
  isEditor = false;
  currentUserEmail = '';
  searchText = '';
  isFavoritesOnly = false;
  isFolderMode = false;
  currentFolder: Folder | null = null;

  get filteredQueries(): Query[] {
    const tokens = this.searchText
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (!tokens.length) {
      return this.queries;
    }
    return this.queries.filter((query) => {
      const haystack = [query.name, query.description ?? '', query.creator_email, ...(query.tags ?? [])]
        .join(' ')
        .toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }

  get currentSortLabelKey(): string {
    return (
      this.sortOptions.find((o) => o.key === this.queryService.currentFilter$.value.sort_by)?.labelKey ??
      'QUERY_SORT_NAME'
    );
  }

  get cardTitle(): string {
    if (this.isFolderMode && this.currentFolder) {
      return this.currentFolder.name;
    }
    if (this.isFavoritesOnly) {
      return 'TAB_MY_FAVORITE_QUERIES';
    }
    return 'TAB_QUERIES';
  }

  ngOnInit(): void {
    const user = this.userService.getUser();
    this.isEditor = user?.can('query_editor') ?? false;
    this.currentUserEmail = user?.getEmail() ?? '';
    this.isFavoritesOnly = this.route.snapshot.data['favoritesOnly'] === true;
    this.isFolderMode = this.route.snapshot.data['folderMode'] === true;

    if (this.isFavoritesOnly) {
      this.folderService.folders$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((f) => (this.folders = f));
      this.folderService.refreshFolders();
    }

    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          if (params['tag']) {
            this.queryService.applyFilter({ ...this.queryService.currentFilter$.value, tags: [params['tag']] });
          }
          if (params['folderId']) {
            this.loadFolderAndQueries(params['folderId']);
          }
        })
      )
      .subscribe();

    if (!this.isFolderMode) {
      this.queryService.currentFilter$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => this.loadQueries())
        )
        .subscribe();
    } else {
      this.queryService.currentFilter$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => {
            const folderId = this.route.snapshot.params['folderId'];
            if (folderId) {
              this.loadFolderQueries(folderId);
            }
          })
        )
        .subscribe();
    }
  }

  loadQueries(): void {
    this.isLoading = true;
    const filter = this.isFavoritesOnly
      ? { ...this.queryService.currentFilter$.value, favorites_only: true }
      : undefined;
    this.queryService
      .getQueries(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (queries) => {
          this.queries = queries;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private loadFolderAndQueries(folderId: string): void {
    this.folderService.getFolders().subscribe((folders) => {
      this.currentFolder = folders.find((f) => f.id === folderId) ?? null;
    });
    this.loadFolderQueries(folderId);
  }

  private loadFolderQueries(folderId: string): void {
    this.isLoading = true;
    this.folderService
      .getFolderQueries(folderId, this.queryService.currentFilter$.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (queries) => {
          this.queries = queries;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  isEditDisabled(query: Query): boolean {
    return query.is_locked && query.creator_email !== this.currentUserEmail;
  }

  toggleFavorite(query: Query, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.queryService.toggleFavorite(query.id).subscribe((res) => {
      if (this.isFavoritesOnly && !res.is_favorite) {
        this.queries = this.queries.filter((q) => q.id !== query.id);
      } else {
        query.is_favorite = res.is_favorite;
      }
      this.messenger.success(__(res.is_favorite ? 'QUERY_FAVORITE_ADDED' : 'QUERY_FAVORITE_REMOVED'));
    });
  }

  openCreateFolderModal(): void {
    this.dialog
      .open(ModalCreateFolderComponent, { data: {}, width: '400px' })
      .afterClosed()
      .pipe(
        tap((name: string | null) => {
          if (!name) {
            return;
          }
          this.folderService
            .createFolder(name)
            .pipe(
              tap(() => {
                this.messenger.success(__('FOLDER_SAVED'));
                this.folderService.refreshFolders();
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({ error: () => this.messenger.error(__('ERROR_OCCURRED')) });
        })
      )
      .subscribe();
  }

  openRenameFolderModal(folder: Folder, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open(ModalCreateFolderComponent, { data: { name: folder.name }, width: '400px' })
      .afterClosed()
      .pipe(
        tap((name: string | null) => {
          if (!name || name === folder.name) {
            return;
          }
          this.folderService
            .renameFolder(folder.id, name)
            .pipe(
              tap(() => {
                this.messenger.success(__('FOLDER_SAVED'));
                this.folderService.refreshFolders();
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({ error: () => this.messenger.error(__('ERROR_OCCURRED')) });
        })
      )
      .subscribe();
  }

  deleteFolder(folder: Folder, event: Event): void {
    event.stopPropagation();
    this.folderService
      .deleteFolder(folder.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messenger.success(__('FOLDER_DELETED'));
          this.folderService.refreshFolders();
        },
        error: () => this.messenger.error(__('ERROR_OCCURRED')),
      });
  }

  navigateToFolder(folder: Folder): void {
    this.router.navigate(['/folder', folder.id]);
  }

  openAddToFolderModal(query: Query, trigger: MatMenuTrigger): void {
    trigger.closeMenu();
    this.folderService
      .getQueryFolders(query.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currentFolderIds) => {
        this.dialog
          .open(ModalAddToFolderComponent, {
            data: { queryId: query.id, currentFolderIds },
            width: '400px',
          })
          .afterClosed()
          .pipe(
            tap((selectedIds: string[] | null) => {
              if (!selectedIds) {
                return;
              }

              const toAttach = selectedIds.filter((id) => !currentFolderIds.includes(id));
              const toDetach = currentFolderIds.filter((id) => !selectedIds.includes(id));

              toAttach.forEach((folderId) => {
                this.folderService
                  .attachQuery(folderId, query.id)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe();
              });
              toDetach.forEach((folderId) => {
                this.folderService
                  .detachQuery(folderId, query.id)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe();
              });

              if (toAttach.length > 0 || toDetach.length > 0) {
                this.folderService.refreshFolders();
              }

              if (this.isFolderMode && this.currentFolder && toDetach.includes(this.currentFolder.id)) {
                this.queries = this.queries.filter((q) => q.id !== query.id);
              }
            })
          )
          .subscribe();
      });
  }

  openDuplicateModal(query: Query, trigger: MatMenuTrigger): void {
    trigger.closeMenu();
    this.dialog
      .open(ModalDuplicateQueryComponent, { data: { name: query.name }, width: '480px' })
      .afterClosed()
      .pipe(
        tap((newName: string | null) => {
          if (!newName) {
            return;
          }
          this.queryService
            .duplicateQuery(query.id, newName)
            .pipe(
              tap(() => {
                this.messenger.success(__('QUERY_DUPLICATED'));
                this.loadQueries();
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({ error: () => this.messenger.error(__('ERROR_OCCURRED')) });
        })
      )
      .subscribe();
  }

  openDeleteModal(query: Query, trigger: MatMenuTrigger): void {
    trigger.closeMenu();
    this.dialog
      .open(ModalConfirmDeleteQueryComponent, { data: { name: query.name }, width: '400px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return [];
          }
          return this.queryService.deleteQuery(query.id).pipe(
            tap(() => {
              this.messenger.success(__('QUERY_DELETED'));
              this.loadQueries();
            })
          );
        })
      )
      .subscribe({ error: () => this.messenger.error(__('ERROR_OCCURRED')) });
  }

  setSortBy(key: 'name' | 'creator_email' | 'created_at'): void {
    this.queryService.applyFilter({ ...this.queryService.currentFilter$.value, sort_by: key });
  }

  goBack(): void {
    this.router.navigate(['/favorites']);
  }

  getAccentClass(query: Query): string {
    return query.color ?? 'accent-green';
  }

  getDatabaseClass(databaseId: string): string {
    return 'db-' + databaseId.toLowerCase().replace(/\s+/g, '-');
  }
}
